import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { api } from '@/lib/api';
import { useSEO } from '@/lib/useSEO';
import { useCart } from '@/lib/cart';
import { Loader2, CheckCircle2, XCircle, ArrowRight, ShoppingBag } from 'lucide-react';

/**
 * Wompi payment result landing.
 *
 * Users are redirected here from Wompi Web Checkout with a `?ref=<reference>`
 * (and optional `id=<wompi_transaction_id>`) query string. The widget's
 * callback is preliminary — the source of truth is the webhook. This page
 * polls `GET /api/payments/wompi/status/{ref}` every 2 s during 60 s while
 * we wait for the webhook to confirm the transaction.
 */
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 60000;

export default function PaymentResult() {
  const [sp] = useSearchParams();
  const reference = sp.get('ref') || sp.get('reference') || sp.get('id') || null;
  const [state, setState] = useState('waiting'); // waiting | approved | declined | timeout | not_ready
  const { clear } = useCart();

  useSEO({
    title: 'Resultado del pago | AutoMarket Colombia',
    description: 'Estamos confirmando tu pago con la pasarela. En unos segundos verás el resultado.',
    canonical: '/pago/resultado',
  });

  useEffect(() => {
    if (!reference) { setState('not_ready'); return; }
    let cancelled = false;
    const startedAt = Date.now();

    const tick = async () => {
      if (cancelled) return;
      try {
        const { data, status } = await api.get(`/payments/wompi/status/${encodeURIComponent(reference)}`, {
          validateStatus: () => true,
        });
        if (status === 404) { setState('not_ready'); return; }
        if (status >= 200 && status < 300 && data) {
          // Backend returns `status` in {paid, cancelled, pending}.
          if (data.status === 'paid') {
            // Éxito confirmado por webhook → vaciamos el carrito (mientras el
            // pago estaba pendiente lo mantuvimos por si Wompi rechazaba y el
            // buyer necesitaba reintentar).
            clear();
            setState('approved');
            return;
          }
          if (data.status === 'cancelled') { setState('declined'); return; }
        }
      } catch (e) {
        // Silent: keep polling.
      }
      if (Date.now() - startedAt >= POLL_TIMEOUT_MS) { setState('timeout'); return; }
      setTimeout(tick, POLL_INTERVAL_MS);
    };
    tick();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference]);

  return (
    <Layout>
      <div className="mx-auto max-w-2xl px-4 py-20 sm:px-6 lg:px-8 text-center" data-testid="payment-result-page">
        {state === 'waiting' && (
          <>
            <Loader2 className="mx-auto h-14 w-14 text-orange-500 animate-spin" />
            <div className="mt-4 text-xs font-bold uppercase tracking-[0.25em] text-orange-600">Procesando</div>
            <h1 className="mt-1 font-display text-3xl font-black tracking-tighter text-slate-900">
              Confirmando tu pago…
            </h1>
            <p className="mt-3 text-slate-600 max-w-md mx-auto">
              Wompi está validando tu transacción. Esto suele tomar solo unos segundos.
              No cierres esta ventana.
            </p>
            {reference && (
              <div className="mt-4 text-[11px] font-mono text-slate-500">
                Ref: {reference}
              </div>
            )}
          </>
        )}

        {state === 'approved' && (
          <>
            <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" />
            <div className="mt-4 text-xs font-bold uppercase tracking-[0.25em] text-emerald-600">Pago aprobado</div>
            <h1 className="mt-1 font-display text-3xl font-black tracking-tighter text-slate-900">
              ¡Tu pago fue confirmado!
            </h1>
            <p className="mt-3 text-slate-600 max-w-md mx-auto">
              Ya generamos tu(s) pedido(s). Puedes ver el detalle en tu panel de compras.
            </p>
            <Link
              to="/mis-pedidos"
              data-testid="payment-result-orders-cta"
              className="mt-6 inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-bold px-6 h-11 transition-colors"
            >
              <ShoppingBag className="h-4 w-4" /> Ir a mis pedidos <ArrowRight className="h-4 w-4" />
            </Link>
          </>
        )}

        {state === 'declined' && (
          <>
            <XCircle className="mx-auto h-14 w-14 text-rose-500" />
            <div className="mt-4 text-xs font-bold uppercase tracking-[0.25em] text-rose-600">Pago no aprobado</div>
            <h1 className="mt-1 font-display text-3xl font-black tracking-tighter text-slate-900">
              El pago no pudo completarse
            </h1>
            <p className="mt-3 text-slate-600 max-w-md mx-auto">
              La pasarela rechazó la transacción. Puedes intentarlo con otro medio de pago
              desde tu carrito.
            </p>
            <Link
              to="/carrito"
              data-testid="payment-result-retry-cta"
              className="mt-6 inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-slate-900 font-bold px-6 h-11 transition-colors"
            >
              Reintentar pago <ArrowRight className="h-4 w-4" />
            </Link>
          </>
        )}

        {(state === 'timeout' || state === 'not_ready') && (
          <>
            <Loader2 className="mx-auto h-14 w-14 text-slate-400" />
            <div className="mt-4 text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Procesando</div>
            <h1 className="mt-1 font-display text-3xl font-black tracking-tighter text-slate-900">
              Tu pago sigue en proceso
            </h1>
            <p className="mt-3 text-slate-600 max-w-md mx-auto">
              Recibirás una notificación cuando la pasarela confirme la operación. Mientras
              tanto puedes revisar tus pedidos.
            </p>
            <Link
              to="/mis-pedidos"
              data-testid="payment-result-orders-cta"
              className="mt-6 inline-flex items-center gap-2 border border-slate-300 hover:bg-slate-50 text-slate-900 font-bold px-6 h-11 transition-colors"
            >
              <ShoppingBag className="h-4 w-4" /> Ver mis pedidos
            </Link>
          </>
        )}
      </div>
    </Layout>
  );
}
